// ===== BEGIN FILE: src/hooks/useChatInsights.ts =====
/**
 * useChatInsights — shared hook for the three Trending Guidance chip handlers.
 *
 * Used by:
 *   - ChatDesktopLayout (desktop 3-col view)
 *   - MobileChatInterface (mobile chip strip)
 *
 * Handlers:
 *   handleEventInsight   — runs probability engine locally, builds ProbabilityWindow[]
 *   handleDataInsight    — instant local markdown render (planet strengths, patterns, etc.)
 *   handleLifeRemediesInsight — fetches Supabase remedies, formats markdown
 *
 * The `onBeforeInsight` callback is called before every handler fires.
 * Desktop passes `() => { setShowQuickQuestions(false); collapseIfUnpinned(); }`.
 * Mobile can pass `() => {}` or omit it.
 */

import { useState, useCallback } from 'react';
import {
  calculateNatalProbability,
  calculateDashaProbability,
  calculateTransitProbability,
  calculateCombinedProbability,
  ACTIVITY_HOUSES,
  type NatalProbabilityInput,
  type ProbabilityWindow,
  type LifeEventType,
} from '../astro/probability/eventProbabilityEngine';
import { getCurrentDashaLevels } from '../utils/vimshottari';
import { calcNatalScore, calcDashaScore, calcTransitScore } from '../astro/probability/lifeAreaForecastEngine';
import { supabase } from '../utils/supabaseClient';
import { DateTime } from 'luxon';
import type { Message } from './useChatMessages';
import { type LifeAreaKey } from '../utils/lifeAreas';

// ─────────────────────────────────────────────────
// ChartInsightData — bundle of pre-computed chart
// data passed from App.tsx to chat components.
// ─────────────────────────────────────────────────
export interface ChartInsightData {
  astroData: any;
  d1HouseScores?: Record<number, number> | null;
  bhavaBalaRupas?: Record<number, number> | null;
  dashaContext?: any;
  utcDateTime?: string;
  vargaMap?: any;
  activeYogas?: any[];
  combust?: Partial<Record<string, boolean>>;
  debil?: Partial<Record<string, boolean>>;
  exalt?: Partial<Record<string, boolean>>;
  functionalNature?: Record<string, string>;
  houseInterpretations?: any[];
  feeds?: any[];
  futureEvents?: { opportunities: any[]; cautions: any[] };
  shadbalaPct?: Record<string, number>;
  svPct?: Record<string, number>;
  shadbalaTotal?: Record<string, number>;
  ishtaKashta?: Record<string, { ishta: number; kashta: number }>;
  bhavaSandhi?: Record<string, number>;
  houseLords?: Record<number, string>;
  houseAspects?: Record<number, string[]>;
}

// ─────────────────────────────────────────────────
// Constants (exported so chip-rendering JSX can use them)
// ─────────────────────────────────────────────────

/** Human-readable labels for event types */
export const EVENT_LABELS: Partial<Record<LifeEventType, string>> = {
  business_start:    '🏢 Career & Business',
  marriage:          '💍 Marriage',
  investment:        '📈 Investment & Wealth',
  property_purchase: '🏡 Property Purchase',
  graha_pravesh:     '🚪 New Home / Relocation',
  surgery:           '🏥 Health & Medical',
  ivf_planning:      '👶 Conception & Children',
  foreign_travel:    '✈️ Foreign Travel',
};

/** Per-life-area event-insight chips */
export const LIFE_AREA_INSIGHT_QUESTIONS: Partial<Record<LifeAreaKey, Array<{ label: string; activity: LifeEventType; displayLabel: string }>>> = {
  job:       [
    { label: '📊 Career Opportunity Periods', activity: 'business_start', displayLabel: 'What are my best job/career windows in the next 12 months?' },
    { label: '🎯 Career strength in my chart',  activity: 'business_start', displayLabel: 'How does my natal chart support my career and job prospects?' },
  ],
  business:  [
    { label: '🏢 Best Time to Launch a Business', activity: 'business_start', displayLabel: 'When are my best windows to launch or grow a business?' },
    { label: '📈 Business Success', activity: 'business_start', displayLabel: 'How does my chart support business success?' },
  ],
  marriage:  [
    { label: '💑 Strong Marriage Timing', activity: 'marriage',          displayLabel: 'What are my best marriage/relationship windows this year?' },
    { label: '💫 Marriage & Relationship Potential',  activity: 'marriage',   displayLabel: 'How does my natal chart support marriage?' },
  ],
  wealth:    [
    { label: '💰 Favorable Investment Periods',      activity: 'investment', displayLabel: 'When are my best periods for investments and wealth creation?' },
    { label: '📊 Favourable Wealth Periods',    activity: 'investment', displayLabel: 'How does my chart support wealth and financial growth?' },
  ],
  property:  [
    { label: '🏡 Best Time for Property Decisions', activity: 'property_purchase', displayLabel: 'When are my best windows to purchase property or real estate?' },
    { label: '🚪 Relocation & Home Change Timing', activity: 'graha_pravesh',     displayLabel: 'When is the best period to move into a new home?' },
  ],
  health:    [
    { label: '⚕️ Strong Health & Energy Periods', activity: 'surgery',    displayLabel: 'What are the key health and vitality windows in my chart over the next 12 months?' },
  ],
  children:  [
    { label: '👶 Family Growth & Conception Timing',      activity: 'ivf_planning', displayLabel: 'What are my best windows for conception and starting a family?' },
  ],
};

/** Per-life-area chip colours */
export const LIFE_AREA_CHIP_COLORS: Record<string, { border: string; hborder: string; bg: string; hbg: string; color: string; shadow: string }> = {
  self:     { border: 'rgba(6,182,212,0.4)',   hborder: 'rgba(6,182,212,0.7)',   bg: 'rgba(6,182,212,0.08)',   hbg: 'rgba(6,182,212,0.17)',   color: '#0e7490', shadow: 'rgba(6,182,212,0.18)'   },
  job:      { border: 'rgba(99,102,241,0.4)',  hborder: 'rgba(99,102,241,0.7)',  bg: 'rgba(99,102,241,0.08)',  hbg: 'rgba(99,102,241,0.18)',  color: '#4338ca', shadow: 'rgba(99,102,241,0.18)'  },
  business: { border: 'rgba(16,185,129,0.4)',  hborder: 'rgba(16,185,129,0.7)',  bg: 'rgba(16,185,129,0.08)',  hbg: 'rgba(16,185,129,0.17)',  color: '#065f46', shadow: 'rgba(16,185,129,0.18)'  },
  marriage: { border: 'rgba(236,72,153,0.4)',  hborder: 'rgba(236,72,153,0.7)',  bg: 'rgba(236,72,153,0.08)',  hbg: 'rgba(236,72,153,0.17)',  color: '#9d174d', shadow: 'rgba(236,72,153,0.18)'  },
  wealth:   { border: 'rgba(212,175,55,0.5)',  hborder: 'rgba(212,175,55,0.8)',  bg: 'rgba(212,175,55,0.1)',   hbg: 'rgba(212,175,55,0.2)',   color: '#92400e', shadow: 'rgba(212,175,55,0.18)'  },
  property: { border: 'rgba(245,158,11,0.4)',  hborder: 'rgba(245,158,11,0.7)',  bg: 'rgba(245,158,11,0.08)',  hbg: 'rgba(245,158,11,0.17)',  color: '#78350f', shadow: 'rgba(245,158,11,0.18)'  },
  health:   { border: 'rgba(16,185,129,0.4)',  hborder: 'rgba(16,185,129,0.7)',  bg: 'rgba(16,185,129,0.08)',  hbg: 'rgba(16,185,129,0.17)',  color: '#065f46', shadow: 'rgba(16,185,129,0.18)'  },
  children: { border: 'rgba(139,92,246,0.4)',  hborder: 'rgba(139,92,246,0.7)',  bg: 'rgba(139,92,246,0.08)',  hbg: 'rgba(139,92,246,0.17)',  color: '#5b21b6', shadow: 'rgba(139,92,246,0.18)'  },
};

// ─────────────────────────────────────────────────
// Hook
// ─────────────────────────────────────────────────

interface UseChatInsightsOptions {
  chartInsightData: ChartInsightData | undefined | null;
  messages: Message[];
  setMessages: React.Dispatch<React.SetStateAction<Message[]>>;
  /** Called at the start of every handler (e.g. collapse sidebar, hide chip panel) */
  onBeforeInsight?: () => void;
  userRole?: 'admin' | 'member';
}

export function useChatInsights({
  chartInsightData,
  messages,
  setMessages,
  onBeforeInsight,
  userRole,
}: UseChatInsightsOptions) {
  const [insightPending, setInsightPending] = useState(false);
  const [insightComputingId, setInsightComputingId] = useState<string | null>(null);
  const [insightSteps, setInsightSteps] = useState<Array<{ text: string; done: boolean }>>([]);
  const [insightAttachments, setInsightAttachments] = useState<Map<string, ProbabilityWindow[]>>(new Map());

  // ── Event Insight (probability engine) ──────────────────────────────────────
  const handleEventInsight = useCallback((activity: LifeEventType, questionDisplay: string) => {
    if (!chartInsightData?.astroData) return;
    onBeforeInsight?.();

    const uid = Date.now().toString();
    const computingId = `insight_computing_${uid}`;
    const resultId   = `insight_result_${uid}`;

    const userMsg: Message = { id: `user_insight_${uid}`, role: 'user',      content: questionDisplay, timestamp: new Date().toISOString() };
    const computingMsg: Message = { id: computingId,              role: 'assistant', content: '__computing__',   timestamp: new Date().toISOString() };
    setMessages(prev => [...prev, userMsg, computingMsg]);
    setInsightPending(true);
    setInsightComputingId(computingId);

    const {
      astroData, d1HouseScores, bhavaBalaRupas, dashaContext,
      utcDateTime: utcDT, vargaMap, activeYogas,
      combust: combustMap, debil, exalt, functionalNature, houseInterpretations,
    } = chartInsightData;

    const ascendantSign: number = astroData.asc.ascendantSignIndex;
    const planets = astroData.placements || astroData.planets;

    const natalInput: NatalProbabilityInput = {
      activity, planets, ascendantSign,
      houseScores: d1HouseScores || undefined,
      bhavaBala: bhavaBalaRupas || undefined,
      vargaData: vargaMap,
      yogas: activeYogas,
      functionalNature: functionalNature || undefined,
      debil: debil as Record<string, boolean> | undefined,
      exalt: exalt as Record<string, boolean> | undefined,
      houseInterpretations: houseInterpretations || undefined,
    };
    const natalResult = calculateNatalProbability(natalInput);

    const moonNatalSign = astroData.grahas?.Moon?.lon !== undefined
      ? Math.floor(((astroData.grahas.Moon.lon % 360) + 360) % 360 / 30) + 1
      : undefined;

    const windows: ProbabilityWindow[] = [];
    const today = new Date();
    for (let week = 0; week < 52; week += 4) {
      const startDate = new Date(today);
      startDate.setDate(today.getDate() + week * 7);
      const endDate = new Date(startDate);
      endDate.setDate(startDate.getDate() + 28);

      let activeDashas = { MD: '', AD: '', PD: '', SD: '' };
      if (utcDT && astroData.grahas?.Moon?.lon !== undefined) {
        try {
          const moonLon = astroData.grahas.Moon.lon;
          const targetISO = DateTime.fromJSDate(startDate).toISO()!;
          const levels = getCurrentDashaLevels(utcDT, moonLon, targetISO);
          if (levels) {
            activeDashas = {
              MD: levels.md.lord,
              AD: levels.ad?.lord || levels.md.lord,
              PD: levels.pd?.lord || levels.ad?.lord || levels.md.lord,
              SD: levels.sd?.lord || '',
            };
          }
        } catch { /* fallthrough to dashaContext */ }
      }
      if (!activeDashas.MD && dashaContext) {
        activeDashas = {
          MD: dashaContext.MD?.planet || '',
          AD: dashaContext.AD?.planet || '',
          PD: dashaContext.PD?.planet || '',
          SD: dashaContext.SD?.planet || '',
        };
      }
      if (!activeDashas.MD) continue;

      const dashaResult = calculateDashaProbability(
        activity, activeDashas, planets, ascendantSign,
        { debil: debil ?? undefined, exalt: exalt ?? undefined, combust: combustMap ?? undefined, functionalNature: functionalNature ?? undefined }
      );
      const transitResult = calculateTransitProbability(activity, startDate, ascendantSign, undefined, moonNatalSign);
      const combined      = calculateCombinedProbability(natalResult.score, dashaResult.score, transitResult.score);

      if (combined.probability >= 25) {
        windows.push({
          startDate, endDate,
          probability: combined.probability, rating: combined.rating,
          natalScore: natalResult.score, dashaScore: dashaResult.score, transitScore: transitResult.score,
          dashaPeriods: activeDashas,
          supportingFactors:  [...natalResult.factors, ...dashaResult.factors, ...transitResult.factors].filter(f => !f.includes('⚠') && !f.includes('Weak')),
          challengingFactors: [...natalResult.factors, ...dashaResult.factors, ...transitResult.factors].filter(f => f.includes('⚠') || f.includes('Weak')),
          keyTransits: transitResult.factors,
        });
      }
    }
    windows.sort((a, b) => b.probability - a.probability);

    const relevantHouses = ACTIVITY_HOUSES[activity] || [];
    const yogaKarakas = Object.entries(functionalNature || {}).filter(([, v]) => v === 'yoga_karaka').map(([k]) => k);
    const currentMD = dashaContext?.MD?.planet || '—';
    const currentAD = dashaContext?.AD?.planet || '—';
    const currentPD = dashaContext?.PD?.planet || '—';

    const steps = [
      `🔍 Reading natal houses… ${relevantHouses.slice(0, 3).map((h: number) => {
        const s = d1HouseScores?.[h];
        return `H${h}: ${s !== undefined ? Math.round(s) + '%' : 'N/A'}`;
      }).join('  ')}`,
      `⚡ Checking Yoga Karakas… ${yogaKarakas.length > 0 ? yogaKarakas.join(', ') + ' detected' : 'None for this lagna'}`,
      `🌟 Scoring planet dignities, karakas & functional nature… Natal ${natalResult.score}%`,
      `🌙 Active dasha: ${currentMD} MD → ${currentAD} AD → ${currentPD} PD`,
      `🪐 Computing Jupiter & Saturn transit positions…`,
      `📅 Scanning 52 weeks for peak probability windows…`,
      `✅ ${windows.length > 0 ? windows.length + ' favorable window' + (windows.length !== 1 ? 's' : '') + ' found!' : 'Analysis complete — see summary below.'}`,
    ];

    setInsightSteps(steps.map(text => ({ text, done: false })));

    (async () => {
      for (let i = 0; i < steps.length; i++) {
        await new Promise<void>(r => setTimeout(r, i === steps.length - 1 ? 300 : 380));
        setInsightSteps(prev => prev.map((s, idx) => idx <= i ? { ...s, done: true } : s));
      }
      await new Promise<void>(r => setTimeout(r, 420));

      const eventLabel = EVENT_LABELS[activity] || activity;
      const top5 = windows.slice(0, 5);

      const currentActiveDashas = { MD: currentMD, AD: currentAD, PD: currentPD, SD: '' };
      const currentDashaResult = calculateDashaProbability(
        activity, currentActiveDashas, planets, ascendantSign,
        { debil: debil ?? undefined, exalt: exalt ?? undefined, combust: combustMap ?? undefined, functionalNature: functionalNature ?? undefined }
      );
      const currentTransitResult = calculateTransitProbability(activity, new Date(), ascendantSign, undefined, moonNatalSign);
      const currentCombined = calculateCombinedProbability(natalResult.score, currentDashaResult.score, currentTransitResult.score);
      const strLabel = (s: number) => s >= 70 ? '✅ Strong' : s >= 50 ? '⚡ Moderate' : '🌱 Building Up';

      let md = `### ${eventLabel} — Vedic Analysis\n\n`;
      md += `| Layer | Score | Strength |\n|---|---|---|\n`;
      md += `| Natal Chart | ${natalResult.score}% | ${strLabel(natalResult.score)} |\n`;
      md += `| Active Dasha | ${currentDashaResult.score}% | ${strLabel(currentDashaResult.score)} |\n`;
      md += `| Current Transit | ${currentTransitResult.score}% | ${strLabel(currentTransitResult.score)} |\n`;
      md += `| Overall Now | ${currentCombined.probability}% | ${currentCombined.rating} |\n`;

      const keyNatal = natalResult.factors.filter(f => !f.includes('⚠') && !f.includes('Weak')).slice(0, 3);
      if (keyNatal.length) md += `\nKey chart factors: ${keyNatal.join(' · ')}\n\n`;

      md += `| Level | Planet | Role |\n|---|---|---|\n`;
      md += `| Mahadasha | ${currentMD} | Life Theme |\n`;
      md += `| Antardasha | ${currentAD} | Active Period |\n`;
      md += `| Pratyantar | ${currentPD} | Near-term Trigger |\n`;
      md += `\n`;

      if (windows.length === 0) {
        md += `> No favorable windows found in the next 12 months. The current dasha combination is not strongly supportive for this area right now. A more favorable period will emerge when the dasha changes.\n\n`;
      } else {
        md += `Top ${top5.length} window${top5.length !== 1 ? 's' : ''} for the next 12 months:\n\n`;
        md += `| # | Period | Probability | Dasha Period | Key Factor |\n|---|---|---|---|---|\n`;
        top5.forEach((w, i) => {
          const dateStr = w.startDate.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
          const ratingEmoji = w.rating === 'Very High' ? '🌸' : w.rating === 'High' ? '🔮' : w.rating === 'Medium' ? '✦' : '○';
          const dashaStr = `${w.dashaPeriods.MD} MD · ${w.dashaPeriods.AD} AD · ${w.dashaPeriods.PD} PD`;
          const topFactor = w.supportingFactors[0] ?? '—';
          md += `| ${i + 1} | ${dateStr} | ${w.probability}% ${ratingEmoji} ${w.rating} | ${dashaStr} | ${topFactor} |\n`;
        });
        md += `\nThe probability chart is shown below. Ask a follow-up question to explore any specific window in depth.\n`;
      }

      const resultMsg: Message = { id: resultId, role: 'assistant', content: md, timestamp: new Date().toISOString() };
      setMessages(prev => prev.map(m => m.id === computingId ? resultMsg : m));
      if (windows.length > 0) {
        setInsightAttachments(prev => new Map(prev).set(resultId, windows));
      } else {
        setInsightAttachments(prev => new Map(prev).set(resultId, []));
      }
      setInsightPending(false);
      setInsightComputingId(null);
      setInsightSteps([]);
    })();
  }, [chartInsightData, setMessages, onBeforeInsight]);

  // ── Data Insight (instant local render) ─────────────────────────────────────
  const handleDataInsight = useCallback((key: 'planet_strengths' | 'powerful_patterns' | 'life_themes' | 'opportunities' | 'year_outlook', userLabel: string) => {
    if (!chartInsightData) return;
    onBeforeInsight?.();
    const uid = Date.now().toString();
    const {
      astroData, d1HouseScores, bhavaBalaRupas, dashaContext,
      functionalNature, combust, debil, exalt, feeds, futureEvents, shadbalaPct: shadPct, activeYogas,
      svPct, shadbalaTotal, ishtaKashta, bhavaSandhi,
    } = chartInsightData;
    const shadbalaPct = chartInsightData.shadbalaPct;
    const PLANETS = ['Sun','Moon','Mars','Mercury','Jupiter','Venus','Saturn','Rahu','Ketu'];

    let md = '';

    if (key === 'planet_strengths') {
      const pFmt = (v: number | undefined) =>
        v !== undefined && Number.isFinite(v) ? `${Math.round(v)}%` : '—';
      const mFmt = (v: number | undefined) =>
        v !== undefined && Number.isFinite(v) ? `${v.toFixed(2)}×` : '—';

      md += `#### Planet Factors (row = factor, columns = planets)\n\n`;
      md += `| Factor / Planet | ${PLANETS.join(' | ')} |\n`;
      md += `|---|${PLANETS.map(() => '---').join('|')}|\n`;
      md += `| Total Shodashvarga % | ${PLANETS.map(p => pFmt(svPct?.[p])).join(' | ')} |\n`;
      md += `| Total Shadbala % | ${PLANETS.map(p => pFmt(shadbalaTotal?.[p])).join(' | ')} |\n`;
      md += `| Ishta / Kashta (0-60) | ${PLANETS.map(p => {
        const ik = ishtaKashta?.[p];
        if (!ik) return '—';
        return `${Math.round(ik.ishta)} / ${Math.round(ik.kashta)}`;
      }).join(' | ')} |\n`;
      if (PLANETS.some(p => bhavaSandhi?.[p] !== undefined)) {
        md += `| Bhava Sandhi × | ${PLANETS.map(p => mFmt(bhavaSandhi?.[p])).join(' | ')} |\n`;
      }
      md += `| Blended total % | ${PLANETS.map(p => pFmt(shadbalaPct?.[p])).join(' | ')} |\n`;

      const ranked = PLANETS
        .filter(p => shadbalaTotal?.[p] !== undefined)
        .sort((a, b) => (shadbalaTotal![b] ?? 0) - (shadbalaTotal![a] ?? 0));
      if (ranked.length >= 2) {
        const strongest = ranked.slice(0, 2).map(p => `${p} (${Math.round(shadbalaTotal![p] ?? 0)}%)`).join(', ');
        const weakest   = ranked.slice(-2).map(p => `${p} (${Math.round(shadbalaTotal![p] ?? 0)}%)`).join(', ');
        md += `\n> Strongest Shadbala: ${strongest} · Weakest: ${weakest}\n`;
      }
      const yogaKarakas = PLANETS.filter(p => functionalNature?.[p] === 'yoga_karaka');
      if (yogaKarakas.length) md += `\n> Yoga Karakas (most powerful for your lagna): ${yogaKarakas.join(', ')}\n`;
    }

    else if (key === 'powerful_patterns') {
      const isAdmin = userRole === 'admin';
      const allYogas = (activeYogas ?? []).filter((f: any) => {
        if (f.private) return isAdmin;
        const msg = (f.message || '').toUpperCase();
        if (msg.includes('ADMIN ONLY') && !isAdmin) return false;
        return true;
      });
      const getTitle = (f: any): string => {
        const raw = f.message?.split(':')[0]?.trim() || f.title || f.ruleKey || 'Yoga';
        return raw.replace(/^[⚠️🌟✦○\s]+/, '').trim();
      };
      const getDesc = (f: any): string => {
        const parts = (f.message || '').split(':');
        const msgDesc = parts.length > 1 ? parts.slice(1).join(':').trim() : '';
        const extraDesc = f.description && f.description !== msgDesc ? f.description : '';
        const combined = [msgDesc, extraDesc].filter(Boolean).join(' | ');
        return combined;
      };
      const sadeSatiItems = allYogas.filter((f: any) => f.ruleKey?.startsWith('sade_sati'));
      const nonSadeSati   = allYogas.filter((f: any) => !f.ruleKey?.startsWith('sade_sati'));
      const yogas  = nonSadeSati.filter((f: any) => (f.score ?? 1) >= 0);
      const doshas = nonSadeSati.filter((f: any) => (f.score ?? 0) < 0);
      md += `### Powerful Patterns in Your Chart\n\n`;
      if (allYogas.length === 0) {
        md += `No classical yogas or doshas detected in your chart.\n`;
      } else {
        if (sadeSatiItems.length) {
          md += `#### 🪐 Sade Sati Status\n\n`;
          sadeSatiItems.forEach((f: any) => {
            const title = getTitle(f);
            const desc  = getDesc(f);
            const scoreLabel = (f.score ?? 0) >= 0 ? '✨ Favorable results due to Saturn\'s natal placement' : '⚠️ Karmic challenge period';
            md += `**${title}**\n`;
            md += `> ${scoreLabel}\n\n`;
            if (desc) md += `> ${desc.substring(0, 600)}\n\n`;
          });
        }
        if (yogas.length) {
          md += `#### Active Yogas (${yogas.length})\n\n`;
          yogas.slice(0, 14).forEach((f: any) => {
            const isNatal = f.isPermanent || f.ruleType === 'natal';
            const tag = isNatal ? 'natal' : 'dasha-active';
            const sev = f.severity === 'high' ? '🌟' : f.severity === 'medium' ? '✦' : '○';
            md += `${sev} **${getTitle(f)}** *(${tag})*\n`;
            const desc = getDesc(f);
            if (desc) md += `> ${desc.substring(0, 400)}\n\n`;
          });
          if (yogas.length > 14) md += `\n*…and ${yogas.length - 14} more yogas detected*\n`;
        }
        if (doshas.length) {
          md += `\n#### Doshas & Challenges (${doshas.length})\n\n`;
          doshas.slice(0, 8).forEach((f: any) => {
            const isNatal = f.isPermanent || f.ruleType === 'natal';
            const tag = isNatal ? 'natal' : 'dasha-active';
            md += `⚠️ **${getTitle(f)}** *(${tag})*\n`;
            const desc = getDesc(f);
            if (desc) md += `> ${desc.substring(0, 400)}\n\n`;
          });
        }
      }
    }

    else if (key === 'life_themes') {
      const AREA_6 = [
        { name: 'Health',   houses: [1, 6, 8, 12],        icon: '❤️',  desc: 'Physical vitality, diseases, chronic issues' },
        { name: 'Wealth',   houses: [1, 2, 5, 9, 11, 12], icon: '💰',  desc: 'Income, assets, speculations, fortune, gains' },
        { name: 'Job',      houses: [1, 6, 10],            icon: '💼',  desc: 'Career, service, professional reputation' },
        { name: 'Business', houses: [1, 3, 7, 11],         icon: '📈',  desc: 'Entrepreneurship, partnerships, ventures' },
        { name: 'Marriage', houses: [1, 7],                icon: '💑',  desc: 'Spouse, partnerships, marital harmony' },
        { name: 'Children', houses: [1, 5],                icon: '👶',  desc: 'Children, creativity, romance' },
      ];
      const avgH = (map: Record<number, number> | null | undefined, hs: number[], fb: number) => {
        const vals = hs.map(h => map?.[h] ?? fb);
        return vals.reduce((a, b) => a + b, 0) / vals.length;
      };
      const ratingLabel = (s: number) => s >= 75 ? 'Excellent' : s >= 60 ? 'Good' : s >= 30 ? 'Fair' : 'Needs Attention';
      const ratingIcon  = (s: number) => s >= 75 ? '✅' : s >= 60 ? '🔵' : s >= 30 ? '🟡' : '🔴';

      md += `#### Your Most Important Life Themes\n\n`;
      md += `| Life Area | Score | Status |\n|---|---|---|\n`;

      const scored = AREA_6.map(area => {
        const bhava        = avgH(bhavaBalaRupas, area.houses, 50);
        const planetImpact = avgH(d1HouseScores, area.houses, 0);
        const normalizedPI = ((planetImpact + 50) / 100) * 100;
        const natalComponent = (bhava + normalizedPI) / 2;
        const combined = Math.round(Math.max(0, Math.min(100, natalComponent * 0.65 + 50 * 0.35)));
        md += `| ${area.icon} ${area.name} | ${combined}% | ${ratingIcon(combined)} ${ratingLabel(combined)} |\n`;
        return { name: area.name, combined };
      });

      const sorted = [...scored].sort((a, b) => b.combined - a.combined);
      const top2    = sorted.slice(0, 2).map(s => `${s.name} (${s.combined}%)`).join(', ');
      const bottom2 = sorted.slice(-2).map(s => `${s.name} (${s.combined}%)`).join(', ');
      md += `\n> Strongest: ${top2}\n> Areas to nurture: ${bottom2}\n`;
      const { MD, AD, PD } = dashaContext ?? {};
      if (MD?.planet) md += `\n> Active dasha: ${MD.planet} MD · ${AD?.planet ?? '—'} AD · ${PD?.planet ?? '—'} PD — shapes the current themes.\n`;
    }

    else if (key === 'opportunities') {
      const fmtDate = (iso: string) => new Date(iso).toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
      md += `### Upcoming Opportunities & Caution Periods\n\n`;
      if (futureEvents && (futureEvents.opportunities.length > 0 || futureEvents.cautions.length > 0)) {
        const opps = futureEvents.opportunities.slice(0, 5);
        const cautions = futureEvents.cautions.slice(0, 5);
        if (opps.length) {
          md += `#### ✨ Opportunity Windows (${futureEvents.opportunities.length} found)\n\n`;
          md += `| # | Period | Area | Title | Confidence |\n|---|---|---|---|---|\n`;
          opps.forEach((f: any, i: number) => {
            const period = [f.window?.from ? fmtDate(f.window.from) : '', f.window?.to ? fmtDate(f.window.to) : ''].filter(Boolean).join(' – ');
            const area = Array.isArray(f.lifeAreas) ? f.lifeAreas.slice(0, 2).join(', ') : '—';
            const conf = f.confidence != null ? `${Math.round(f.confidence)}%` : '—';
            md += `| ${i + 1} | ${period} | ${area} | ${f.title ?? '—'} | ${conf} |\n`;
          });
          md += `\n`;
          opps.forEach((f: any, i: number) => {
            if (f.description) md += `**${i + 1}. ${f.title}**: ${f.description.split('|')[0].trim()}\n`;
          });
        } else {
          md += `No strong opportunity windows found in upcoming periods.\n`;
        }
        if (cautions.length) {
          md += `\n#### ⚠️ Caution Time Windows (${futureEvents.cautions.length} found)\n\n`;
          md += `| # | Period | Area | Title | Confidence |\n|---|---|---|---|---|\n`;
          cautions.forEach((f: any, i: number) => {
            const period = [f.window?.from ? fmtDate(f.window.from) : '', f.window?.to ? fmtDate(f.window.to) : ''].filter(Boolean).join(' – ');
            const area = Array.isArray(f.lifeAreas) ? f.lifeAreas.slice(0, 2).join(', ') : '—';
            const conf = f.confidence != null ? `${Math.round(f.confidence)}%` : '—';
            md += `| ${i + 1} | ${period} | ${area} | ${f.title ?? '—'} | ${conf} |\n`;
          });
          md += `\n`;
          cautions.forEach((f: any, i: number) => {
            if (f.description) md += `**${i + 1}. ${f.title}**: ${f.description.split('|')[0].trim()}\n`;
          });
        } else {
          md += `\nNo major caution periods detected in upcoming windows.\n`;
        }
        const { MD, AD } = dashaContext ?? {};
        if (MD?.planet) md += `\n> Active dasha: ${MD.planet} MD · ${AD?.planet ?? '—'} AD — shapes the overall timing of these events.\n`;
      } else {
        const now = new Date();
        const upcoming = (feeds ?? []).filter((f: any) => {
          if (!f.window?.to) return false;
          try { return new Date(f.window.to) >= now; } catch { return false; }
        });
        const opps = upcoming.filter((f: any) => (f.score ?? 0) >= 30).sort((a: any, b: any) => (b.score ?? 0) - (a.score ?? 0));
        const cautions = upcoming.filter((f: any) => (f.score ?? 0) <= -30).sort((a: any, b: any) => (a.score ?? 0) - (b.score ?? 0));
        if (opps.length) {
          md += `#### Opportunities — top ${Math.min(3, opps.length)}\n\n`;
          md += `| # | Period | Area | Note |\n|---|---|---|---|\n`;
          opps.slice(0, 3).forEach((f: any, i: number) => {
            const period = [f.window?.from ? fmtDate(f.window.from) : '', f.window?.to ? fmtDate(f.window.to) : ''].filter(Boolean).join(' – ');
            const area = Array.isArray(f.lifeAreas) ? f.lifeAreas.join(', ') : (f.lifeArea ?? '—');
            md += `| ${i + 1} | ${period} | ${area} | ${f.shortMessage || f.ruleKey || '—'} |\n`;
          });
        } else { md += `No strong opportunity windows found in upcoming periods.\n`; }
        if (cautions.length) {
          md += `\n#### Caution Periods — top ${Math.min(3, cautions.length)}\n\n`;
          md += `| # | Period | Area | Note |\n|---|---|---|---|\n`;
          cautions.slice(0, 3).forEach((f: any, i: number) => {
            const period = [f.window?.from ? fmtDate(f.window.from) : '', f.window?.to ? fmtDate(f.window.to) : ''].filter(Boolean).join(' – ');
            const area = Array.isArray(f.lifeAreas) ? f.lifeAreas.join(', ') : (f.lifeArea ?? '—');
            md += `| ${i + 1} | ${period} | ${area} | ${f.shortMessage || f.ruleKey || '—'} |\n`;
          });
        } else { md += `\nNo major caution periods detected in upcoming windows.\n`; }
      }
    }

    else if (key === 'year_outlook') {
      const AREAS = [
        { icon: '🏥', label: 'Health',    houses: [1, 6, 8, 12] },
        { icon: '💰', label: 'Wealth',    houses: [1, 2, 5, 9, 11, 12] },
        { icon: '💼', label: 'Job',       houses: [1, 6, 10] },
        { icon: '🏢', label: 'Business',  houses: [1, 3, 7, 11] },
        { icon: '💑', label: 'Marriage',  houses: [1, 7] },
        { icon: '👶', label: 'Children',  houses: [1, 5] },
        { icon: '🏡', label: 'Home',      houses: [1, 4] },
        { icon: '📚', label: 'Education', houses: [2, 4, 5] },
      ];
      const _ad       = astroData;
      const _hs       = d1HouseScores;
      const _fn       = functionalNature as Record<string, string> | undefined;
      const _birthISO = chartInsightData.utcDateTime;
      const _moonLon  = (_ad?.grahas?.Moon?.lon ?? _ad?.grahas?.Moon?.siderealLon ?? 0) as number;
      const _ascSign  = (_ad?.asc?.ascendantSignIndex ?? 1) as number;
      const _planetMap: Record<string, { house: number; sign: number }> = {};
      if (_ad?.placements) {
        for (const [pl, d] of Object.entries(_ad.placements as Record<string, any>)) {
          if ((d as any)?.house) _planetMap[pl] = { house: Number((d as any).house), sign: Number((d as any).sign ?? Math.floor(((d as any).lon ?? 0) / 30) + 1) };
        }
      }
      const _normHS: Record<number, number> = {};
      for (let h = 1; h <= 12; h++) {
        _normHS[h] = Math.max(0, Math.min(100, ((_hs as any)?.[h] ?? 0) + 50));
      }
      const now = new Date();
      const quarters = [0, 3, 6, 9].map(offset => {
        const s = new Date(now); s.setMonth(now.getMonth() + offset);
        const e = new Date(s);   e.setMonth(s.getMonth() + 3);
        const mid = new Date(s); mid.setMonth(s.getMonth() + 1, 15);
        const lbl = s.toLocaleDateString('en-US', { month: 'short', year: '2-digit' }) + '–' +
                    e.toLocaleDateString('en-US', { month: 'short', year: '2-digit' });
        return { label: lbl, mid };
      });
      md += `### Life Outlook — Next 12 Months\n\n`;
      md += `| Life Area | ${quarters.map(q => q.label).join(' | ')} |\n`;
      md += `|---|${quarters.map(() => ':---:').join('|')}|\n`;
      AREAS.forEach(area => {
        const natalScore = calcNatalScore(area.houses, _normHS);
        const cells = quarters.map(q => {
          let dashaScore = 50;
          try {
            if (_birthISO && _moonLon !== undefined) {
              const levels = getCurrentDashaLevels(_birthISO, _moonLon, q.mid.toISOString());
              if (levels?.md) {
                const periods = {
                  MD: levels.md.lord as string,
                  AD: (levels.ad?.lord ?? levels.md.lord) as string,
                  PD: (levels.pd?.lord ?? levels.ad?.lord ?? levels.md.lord) as string,
                  SD: levels.sd?.lord as string | undefined,
                };
                dashaScore = calcDashaScore(area.houses, periods, _planetMap, _ascSign, _fn);
              }
            }
          } catch { /* fallback 50 */ }
          const transitScore = calcTransitScore(area.houses, q.mid, _ascSign, undefined, _fn);
          const combined = Math.round(natalScore * 0.3 + dashaScore * 0.5 + transitScore * 0.2);
          return combined >= 65 ? '🟢' : combined <= 44 ? '🔴' : '🟡';
        });
        md += `| ${area.icon} ${area.label} | ${cells.join(' | ')} |\n`;
      });
      md += `\n> 🟢 Favorable &nbsp;·&nbsp; 🟡 Mixed &nbsp;·&nbsp; 🔴 Challenging\n`;
      const { MD, AD } = dashaContext ?? {};
      if (MD?.planet) md += `\n> Active dasha: ${MD.planet} MD · ${AD?.planet ?? '—'} AD — shapes the overall tone for this period.\n`;
    }

    const userMsg: Message = { id: `di_user_${uid}`, role: 'user', content: userLabel, timestamp: new Date().toISOString() };
    const botMsg: Message  = { id: `di_bot_${uid}`,  role: 'assistant', content: md,        timestamp: new Date().toISOString() };
    setMessages(prev => [...prev, userMsg, botMsg]);
    setInsightAttachments(prev => new Map(prev).set(`di_bot_${uid}`, []));
  }, [chartInsightData, setMessages, onBeforeInsight, userRole]);

  // ── Life Remedies Insight ────────────────────────────────────────────────────
  const handleLifeRemediesInsight = useCallback((lifeAreaKey: LifeAreaKey) => {
    if (!chartInsightData) return;
    onBeforeInsight?.();

    const AREA_META: Partial<Record<LifeAreaKey, { name: string; houses: number[]; icon: string }>> = {
      health:    { name: 'Health',    houses: [1, 6, 8, 12],        icon: '❤️' },
      wealth:    { name: 'Wealth',    houses: [1, 2, 5, 9, 11, 12], icon: '💰' },
      job:       { name: 'Job',       houses: [1, 6, 10],           icon: '💼' },
      business:  { name: 'Business',  houses: [1, 3, 7, 11],        icon: '📈' },
      marriage:  { name: 'Marriage',  houses: [1, 7],               icon: '💑' },
      children:  { name: 'Children',  houses: [1, 5],               icon: '👶' },
      self:      { name: 'Self',      houses: [1, 5, 9],            icon: '🪬' },
      education: { name: 'Education', houses: [2, 4, 5],            icon: '📚' },
      property:  { name: 'Property',  houses: [1, 4, 11, 12],       icon: '🏡' },
      mother:    { name: 'Mother',    houses: [4, 10],              icon: '🤱' },
      father:    { name: 'Father',    houses: [9, 10],              icon: '👨' },
    };
    const areaMeta = AREA_META[lifeAreaKey];
    if (!areaMeta) return;

    const uid = Date.now().toString();
    const computingId = `rem_computing_${uid}`;
    const resultId    = `rem_result_${uid}`;

    const userMsg: Message = { id: `rem_user_${uid}`, role: 'user', content: `🌿 Show Important Remedies — ${areaMeta.icon} ${areaMeta.name}`, timestamp: new Date().toISOString() };
    const computingMsg: Message = { id: computingId, role: 'assistant', content: '__computing__', timestamp: new Date().toISOString() };
    setMessages(prev => [...prev, userMsg, computingMsg]);
    setInsightPending(true);
    setInsightComputingId(computingId);
    setInsightSteps([
      { text: `🏠 Reading ${areaMeta.name} houses (${areaMeta.houses.map(h => `H${h}`).join(', ')})…`, done: false },
      { text: '🪐 Identifying residing, aspecting & lord planets…', done: false },
      { text: '🌿 Fetching Important Remedies from database…', done: false },
    ]);

    const houseLords   = chartInsightData.houseLords   ?? {} as Record<number, string>;
    const houseAspects = chartInsightData.houseAspects ?? {} as Record<number, string[]>;
    const shadbalaPct  = chartInsightData.shadbalaPct  ?? {} as Record<string, number>;
    const functionalNature = chartInsightData.functionalNature ?? {} as Record<string, string>;
    const d1HouseScores    = chartInsightData.d1HouseScores    ?? {} as Record<number, number>;

    const placements: Record<string, number> = {};
    Object.entries(chartInsightData.astroData.placements).forEach(([p, v]) => { placements[p] = (v as any).house; });

    const fnColor = (p: string) => {
      const fn = functionalNature[p];
      if (fn === 'yoga_karaka') return '✨';
      if (fn === 'benefic')     return '🟢';
      if (fn === 'malefic')     return '🔴';
      return '⚪';
    };
    const fnLabel = (p: string) => {
      const fn = functionalNature[p];
      if (fn === 'yoga_karaka') return 'Yoga Karaka';
      if (fn === 'benefic')     return 'Functional Benefic';
      if (fn === 'malefic')     return 'Functional Malefic';
      return 'Neutral';
    };
    const strengthText = (p: string) => {
      const s = shadbalaPct[p];
      return s != null ? ` **${s > 0 ? '+' : ''}${Math.round(s)}**` : '';
    };

    const residing: { planet: string; house: number }[] = Object.entries(placements)
      .filter(([, h]) => areaMeta.houses.includes(h))
      .map(([planet, house]) => ({ planet, house }));

    const aspecting: { planet: string; house: number }[] = areaMeta.houses.flatMap(house =>
      (houseAspects[house] || []).map(planet => ({ planet, house }))
    );

    const lords: { house: number; lord: string }[] = areaMeta.houses.map(h => ({
      house: h, lord: houseLords[h] ?? '?',
    }));

    (async () => {
      await new Promise<void>(r => setTimeout(r, 320));
      setInsightSteps(prev => prev.map((s, i) => i === 0 ? { ...s, done: true } : s));
      await new Promise<void>(r => setTimeout(r, 300));
      setInsightSteps(prev => prev.map((s, i) => i <= 1 ? { ...s, done: true } : s));

      const allPlanets = new Set([
        ...Object.keys(placements),
        ...Object.values(houseLords),
      ]);
      const cacheKey = `remedies_planets_${Array.from(allPlanets).sort().join('_')}_v1`;
      let rawRemedies: any[] = [];
      try {
        const cached = localStorage.getItem(cacheKey);
        if (cached) {
          rawRemedies = JSON.parse(cached);
        } else {
          const { data, error } = await supabase
            .from('remedies')
            .select('id, name, title, description, category, cost_level, related_entity')
            .eq('related_to', 'planet')
            .in('related_entity', Array.from(allPlanets))
            .eq('is_active', true)
            .neq('category', 'other')
            .order('priority', { ascending: false });
          if (!error) {
            rawRemedies = data || [];
            localStorage.setItem(cacheKey, JSON.stringify(rawRemedies));
          }
        }
      } catch { /* fallthrough */ }

      setInsightSteps(prev => prev.map(s => ({ ...s, done: true })));
      await new Promise<void>(r => setTimeout(r, 280));

      const remedyByPlanet: Record<string, Array<{ title: string; description: string; category: string; cost_level?: string; house?: number }>> = {};
      for (const r of rawRemedies) {
        const houseMatch = (r.name || '').match(/House (\d+)/);
        const house = houseMatch ? parseInt(houseMatch[1], 10) : undefined;
        if (!remedyByPlanet[r.related_entity]) remedyByPlanet[r.related_entity] = [];
        remedyByPlanet[r.related_entity].push({ ...r, house });
      }

      const bestRemedy = (planet: string, actualHouse?: number) => {
        const list = remedyByPlanet[planet] || [];
        return list.find(r => r.house === actualHouse)
          || list.find(r => !r.house || r.house === 0)
          || null;
      };

      const excludedCats = new Set(['rudraksha', 'gemstone', 'mantra', 'worship', 'donation']);
      const mandatoryPlanets = new Set<string>();
      const mandatoryRemedyMap = new Map<string, ReturnType<typeof bestRemedy>>();

      for (const house of areaMeta.houses) {
        const inHouse = residing.filter(r => r.house === house);
        if (inHouse.length > 0) {
          for (const { planet } of inHouse) {
            const rem = bestRemedy(planet, house);
            if (!rem) continue;
            const cat = (rem.category || '').toLowerCase();
            if (excludedCats.has(cat)) continue;
            if (cat === 'lifestyle') { mandatoryPlanets.add(planet); mandatoryRemedyMap.set(planet, rem); continue; }
            const str = shadbalaPct[planet];
            if (str != null && str < 0) { mandatoryPlanets.add(planet); mandatoryRemedyMap.set(planet, rem); }
          }
        } else {
          const aspectingHouse = aspecting.filter(a => a.house === house);
          if (aspectingHouse.length > 0) {
            for (const { planet } of aspectingHouse) {
              const actualHouse = placements[planet];
              const rem = bestRemedy(planet, actualHouse);
              if (!rem) continue;
              mandatoryPlanets.add(planet);
              if (!mandatoryRemedyMap.has(planet)) mandatoryRemedyMap.set(planet, rem);
            }
          } else {
            const lord = houseLords[house];
            if (lord) {
              const actualHouse = placements[lord];
              const rem = bestRemedy(lord, actualHouse);
              if (rem) { mandatoryPlanets.add(lord); if (!mandatoryRemedyMap.has(lord)) mandatoryRemedyMap.set(lord, rem); }
            }
          }
        }
      }

      const areaScore = areaMeta.houses.length > 0
        ? Math.round(areaMeta.houses.map(h => d1HouseScores[h] ?? 0).reduce((a, b) => a + b, 0) / areaMeta.houses.length)
        : 0;
      const scoreLabel = areaScore >= 15 ? '✅ Strong' : areaScore >= 0 ? '🟡 Moderate' : areaScore >= -15 ? '🟠 Challenging' : '🔴 Needs Attention';

      let md = `### 🌿 Important Remedies — ${areaMeta.icon} ${areaMeta.name}\n\n`;
      md += `**Houses:** ${areaMeta.houses.join(', ')}  ·  **Area Score:** ${areaScore > 0 ? '+' : ''}${areaScore}  ${scoreLabel}\n\n`;

      md += `#### 🪐 Influencing Planets\n\n`;
      if (residing.length > 0) {
        md += `**Residing** (planets in area houses):\n`;
        for (const { planet, house } of residing) {
          md += `- ${fnColor(planet)} **${planet}** in H${house}${strengthText(planet)} — ${fnLabel(planet)}\n`;
        }
        md += '\n';
      }
      if (aspecting.length > 0) {
        const seen = new Set<string>();
        const uniqueAspecting = aspecting.filter(a => { if (seen.has(a.planet)) return false; seen.add(a.planet); return true; });
        md += `**Aspecting** (aspects the area houses):\n`;
        for (const { planet, house } of uniqueAspecting) {
          md += `- ${fnColor(planet)} **${planet}** → H${house}${strengthText(planet)} — ${fnLabel(planet)}\n`;
        }
        md += '\n';
      }
      md += `**Lords** (house rulers for this area):\n`;
      for (const { house, lord } of lords) {
        const lordHouse = placements[lord] ?? '?';
        md += `- ${fnColor(lord)} **${lord}** (H${house} lord) placed in H${lordHouse}${strengthText(lord)} — ${fnLabel(lord)}\n`;
      }
      md += '\n---\n\n';

      const mandatoryList = Array.from(mandatoryPlanets)
        .map(p => ({ planet: p, remedy: mandatoryRemedyMap.get(p) }))
        .filter(x => x.remedy);

      if (mandatoryList.length === 0) {
        md += `> No specific "important" remedies are flagged for this life area right now.\n`;
      } else {
        md += `#### 🌿 Important Remedies (${mandatoryList.length})\n\n`;
        for (const { planet, remedy } of mandatoryList) {
          if (!remedy) continue;
          const cat = remedy.category ? `\`${remedy.category}\`` : '';
          const cost = remedy.cost_level ? ` · ${remedy.cost_level}` : '';
          const isNegative = (shadbalaPct[planet] ?? 1) < 0;
          const prefix = isNegative ? '⚠️ ' : '';
          md += `**${prefix}${planet}** ${fnColor(planet)} ${cat}${cost}\n\n`;
          md += `> ${remedy.title || ''}\n\n`;
          if (remedy.description) md += `${remedy.description}\n\n`;
        }
      }

      const resultMsg: Message = { id: resultId, role: 'assistant', content: md, timestamp: new Date().toISOString() };
      setMessages(prev => prev.map(m => m.id === computingId ? resultMsg : m));
      setInsightAttachments(prev => new Map(prev).set(resultId, []));
      setInsightPending(false);
      setInsightComputingId(null);
      setInsightSteps([]);
    })();
  }, [chartInsightData, setMessages, onBeforeInsight]);

  return {
    insightAttachments,
    insightPending,
    insightComputingId,
    insightSteps,
    handleEventInsight,
    handleDataInsight,
    handleLifeRemediesInsight,
  };
}
